import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import { listGenerationLedger } from '@platform/client';
import { generationLedgerPageSchema } from '@platform/schema/generation-ledger';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { FilterSelect, sameAs } from '../library/FilterSelect';
import { PAGE, Pager } from '../library/Pager';
import { PageHeader, SCREEN_GAP } from '../routes/PageHeader';

import { GenerationPatterns } from './GenerationPatterns';
import { STATUS } from './Scorecard';

const MODEL_WIDTH = 220;
const RESEARCH_WIDTH = 140;

/** Builds with research on beside builds without — GEN-218. */
const RESEARCH = [
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
];

/**
 * Every lesson build, one row each, to compare one config or card with another — GEN-186.
 * The numbers are the scorecard's when the build's last review settled.
 */
export function GenerationLedger() {
  const [model, setModel] = useState('');
  const [research, setResearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const page = useQuery({
    queryKey: ['generation-ledger', model, research, from, to, offset],
    queryFn: async () =>
      generationLedgerPageSchema.parse(
        (
          await listGenerationLedger({
            query: {
              limit: PAGE,
              offset,
              ...(model === '' ? {} : { model }),
              ...(research === '' ? {} : { research }),
              ...(from === '' ? {} : { from }),
              ...(to === '' ? {} : { to }),
            },
            throwOnError: true,
          })
        ).data,
      ),
  });

  const rows = page.data?.rows ?? [];
  const filtered = (set: (value: string) => void) => (value: string) => {
    set(value);
    setOffset(0);
  };

  return (
    <Stack spacing={SCREEN_GAP}>
      <PageHeader
        title="Generation ledger"
        subtitle="One row per lesson build, newest first, scored when its last review settled."
      />

      <GenerationPatterns />

      <Stack direction="row" spacing={2}>
        <FilterSelect
          label="Model"
          value={model}
          options={sameAs(page.data?.models ?? [])}
          width={MODEL_WIDTH}
          onChange={filtered(setModel)}
        />
        <FilterSelect
          label="Research"
          value={research}
          options={RESEARCH}
          width={RESEARCH_WIDTH}
          onChange={filtered(setResearch)}
        />
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={(event) => {
            filtered(setFrom)(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={(event) => {
            filtered(setTo)(event.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </Stack>

      {page.isPending && <LinearProgress />}
      {page.isError && (
        <Alert severity="error">The ledger could not be loaded.</Alert>
      )}
      {page.isSuccess && rows.length === 0 && (
        <Alert severity="info">No build matches.</Alert>
      )}

      {rows.length > 0 && (
        <Table size="small" aria-label="Generation ledger">
          <TableHead>
            <TableRow>
              <TableCell align="right">#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Lesson</TableCell>
              <TableCell>Research</TableCell>
              <TableCell>Writer</TableCell>
              <TableCell>Reviewer</TableCell>
              <TableCell align="right">QA</TableCell>
              <TableCell>Verdict</TableCell>
              <TableCell align="right">Writing</TableCell>
              <TableCell align="right">Blockers</TableCell>
              <TableCell align="right">Minutes</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.id} hover>
                <TableCell align="right">{offset + index + 1}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {new Date(row.createdAt).toLocaleString()}
                </TableCell>
                <TableCell>{row.lessonTitle}</TableCell>
                <TableCell>{row.research ? 'On' : 'Off'}</TableCell>
                <TableCell>{row.writer.model ?? '—'}</TableCell>
                <TableCell>{row.reviewer.model ?? '—'}</TableCell>
                <TableCell align="right">{row.qa.overall}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STATUS[row.qa.status].label}
                    color={STATUS[row.qa.status].colour}
                  />
                </TableCell>
                <TableCell align="right">{row.qa.scores.WRITING}</TableCell>
                <TableCell align="right">{row.qa.blockers}</TableCell>
                <TableCell align="right">{row.minutes.toFixed(1)}</TableCell>
                <TableCell>
                  {row.lessonId === null ? (
                    'Deleted'
                  ) : (
                    <Link
                      component={RouterLink}
                      to={`/authoring/lessons/${row.lessonId}`}
                    >
                      Open lesson
                    </Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Pager
        offset={offset}
        total={page.data?.total ?? 0}
        onOffset={setOffset}
      />
    </Stack>
  );
}
